import { AnyObject } from '@xbcb/shared-types';
import { VneRequirements } from 'libs/pgaHelpers/epa';
import {
  ValidationErrorType,
  type AdditionalFormError,
  type NamePath,
} from '@xbcb/ui-types';

// Catches EPA VNE data that CBP rejects, or that crashes PGA construction, so
// the operator sees it in the form instead of at submission.
// see: https://t.corp.amazon.com/V2331106755
export const validateEpaVne = ({
  pga,
  tariffNamePath,
  errorPrefix = '',
}: {
  pga: AnyObject;
  tariffNamePath: NamePath;
  errorPrefix?: string;
}): { additionalErrors: AdditionalFormError[] } => {
  const additionalErrors: AdditionalFormError[] = [];

  if (pga?.agencyCode !== 'EPA' || pga?.programCode !== 'VNE' || !pga.vehicle) {
    return { additionalErrors };
  }

  const hasVehicleItemIdentity = [
    pga.vehicle.vehicleIdentificationNumbers,
    pga.vehicle.serialNumbers,
  ].some((identities) => identities?.some((identity: string) => identity));

  if (!hasVehicleItemIdentity) {
    additionalErrors.push({
      title: 'Vehicle Identification Missing',
      type: ValidationErrorType.GENERAL,
      path: tariffNamePath,
      messages: [
        `${errorPrefix}EPA Vehicles and Engines requires at least one VIN or Vehicle Serial Number`,
      ],
    });
  }

  // No test group means no LPCO, which CBP rejects as "MISSING PG14 PER PGA".
  const needsTestGroup = VneRequirements.isTestGroupRequiredForNonRoadEngine(
    pga.vneImportCode,
    pga.vneIndustryCode,
  );
  if (needsTestGroup && !pga.epaTestGroup) {
    additionalErrors.push({
      title: 'Test Group Missing',
      type: ValidationErrorType.GENERAL,
      path: tariffNamePath,
      messages: [
        `${errorPrefix}EPA Vehicles and Engines requires a Test Group (engine family) for non-road engines`,
      ],
    });
  }

  return { additionalErrors };
};
